import Ionicons from "@expo/vector-icons/Ionicons";
import type { ComponentProps } from "react";
import {
  ActivityIndicator,
  Pressable,
  StyleSheet,
  Text,
  View,
} from "react-native";

import { Modal } from "@/components/ui/app-modal";
import { useTranslation } from "react-i18next";

import { BlurredBackdrop } from "@/components/ui/blurred-backdrop";
import { Radius, Spacing } from "@/constants/theme";
import { useTheme } from "@/hooks/use-theme";

type IoniconName = ComponentProps<typeof Ionicons>["name"];

export type ConfirmModalProps = {
  visible: boolean;
  title: string;
  message?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Style the confirm action as destructive (red). */
  destructive?: boolean;
  /** Show a spinner on the confirm button and block interaction. */
  busy?: boolean;
  icon?: IoniconName;
  /** "filled" = solid background; "ghost" = no fill (outlined). */
  confirmVariant?: "filled" | "ghost";
  cancelVariant?: "filled" | "ghost";
  /** Override the confirm/cancel accent colour (e.g. green for a safe action). */
  confirmColor?: string;
  cancelColor?: string;
  onConfirm: () => void;
  onCancel: () => void;
};

/**
 * App-styled confirmation dialog: a centered card over the standard blurred
 * backdrop — the same look as the app's other modals (see StayVisibleModal).
 * Replaces the OS/browser `confirm()` alert for in-app confirmations.
 */
export function ConfirmModal({
  visible,
  title,
  message,
  confirmLabel,
  cancelLabel,
  destructive = false,
  busy = false,
  icon,
  confirmVariant = "filled",
  cancelVariant = "ghost",
  confirmColor,
  cancelColor,
  onConfirm,
  onCancel,
}: ConfirmModalProps) {
  const { t } = useTranslation();
  const resolvedConfirmLabel = confirmLabel ?? t("profile.a11y.confirm");
  const resolvedCancelLabel = cancelLabel ?? t("common.cancel");
  const c = useTheme();
  const accent = destructive ? c.danger : c.tint;

  // A button is either filled (solid `color` background, onTint text) or ghost
  // (no fill, `color`-outlined with `color` text).
  const buttonStyle = (variant: "filled" | "ghost", color: string) =>
    variant === "filled"
      ? { backgroundColor: color, borderColor: color }
      : { backgroundColor: "transparent", borderColor: color };
  const buttonTextColor = (variant: "filled" | "ghost", color: string) =>
    variant === "filled" ? c.onTint : color;

  const confirmAccent = confirmColor ?? accent;
  const cancelAccent = cancelColor ?? c.border;

  const confirmButton = (
    <Pressable
      key="confirm"
      accessibilityRole="button"
      disabled={busy}
      onPress={onConfirm}
      style={({ pressed }) => [
        styles.actionBtn,
        buttonStyle(confirmVariant, confirmAccent),
        busy && styles.disabled,
        pressed && !busy && styles.pressed,
      ]}
    >
      {busy ? (
        <ActivityIndicator
          size="small"
          color={buttonTextColor(confirmVariant, confirmAccent)}
        />
      ) : (
        <Text
          style={[
            styles.actionText,
            { color: buttonTextColor(confirmVariant, confirmAccent) },
          ]}
        >
          {resolvedConfirmLabel}
        </Text>
      )}
    </Pressable>
  );

  const cancelButton = (
    <Pressable
      key="cancel"
      accessibilityRole="button"
      disabled={busy}
      onPress={onCancel}
      style={({ pressed }) => [
        styles.actionBtn,
        buttonStyle(cancelVariant, cancelAccent),
        pressed && !busy && styles.pressed,
      ]}
    >
      <Text
        style={[
          styles.actionText,
          {
            color:
              cancelVariant === "filled" ? c.onTint : (cancelColor ?? c.text),
          },
        ]}
      >
        {resolvedCancelLabel}
      </Text>
    </Pressable>
  );

  // For destructive dialogs, put the safe (cancel) action first so it's the
  // easiest to reach and the destructive action isn't the default target.
  const buttons = destructive
    ? [cancelButton, confirmButton]
    : [confirmButton, cancelButton];

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      statusBarTranslucent
      onRequestClose={busy ? undefined : onCancel}
    >
      <View style={styles.screen}>
        <BlurredBackdrop
          accessibilityLabel={t("profile.a11y.dismiss")}
          disabled={busy}
          style={styles.backdrop}
          onPress={onCancel}
        />

        <View
          style={[
            styles.sheet,
            { backgroundColor: c.surface, borderColor: c.border },
          ]}
        >
          {icon ? (
            <View
              style={[
                styles.icon,
                {
                  backgroundColor: destructive
                    ? c.dangerTint
                    : c.backgroundSelected,
                },
              ]}
            >
              <Ionicons name={icon} size={24} color={accent} />
            </View>
          ) : null}

          <Text style={[styles.title, { color: c.text }]}>{title}</Text>
          {message ? (
            <Text style={[styles.message, { color: c.textSecondary }]}>
              {message}
            </Text>
          ) : null}

          <View style={styles.actions}>{buttons}</View>
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  screen: { flex: 1, alignItems: "center", justifyContent: "center" },
  backdrop: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(8, 15, 26, 0.55)",
  },
  sheet: {
    width: "88%",
    maxWidth: 420,
    padding: Spacing.xl,
    gap: Spacing.sm,
    alignItems: "center",
    borderRadius: Radius.xl,
    borderWidth: 1,
  },
  icon: {
    width: 56,
    height: 56,
    borderRadius: 28,
    alignItems: "center",
    justifyContent: "center",
    marginBottom: Spacing.xs,
  },
  title: {
    fontSize: 19,
    fontWeight: "800",
    textAlign: "center",
  },
  message: {
    fontSize: 15,
    lineHeight: 22,
    textAlign: "center",
  },
  actions: { width: "100%", gap: Spacing.sm, marginTop: Spacing.md },
  actionBtn: {
    minHeight: 50,
    alignItems: "center",
    justifyContent: "center",
    borderRadius: Radius.lg,
    borderWidth: 1,
  },
  actionText: { fontSize: 16, fontWeight: "800" },
  disabled: { opacity: 0.7 },
  pressed: { opacity: 0.85 },
});
